import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot, setDoc, getDoc } from 'firebase/firestore';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { db, auth, initializeFirebase } from '@/lib/firebase';
import { useAdmin } from '@/hooks/useAdmin';

interface NetworkFees {
  burnFee: string;
  lockFee: string;
  toolsFee: string;
  scanFee: string;
  masterLockFee: string;
  ttTaxDistribution: string;
  addLiquidityFee: string;
  removeLiquidityFee: string;
  multiSendFee: string;
  logoFee?: string;
}

interface FeeSettings {
  networkFees: {
    [chainId: number]: NetworkFees;
  };
  collectors: {
    address: string;
    share: number;
  }[];
}

interface FirebaseFeeContextType {
  settings: FeeSettings | null;
  loading: boolean;
  error: string | null;
  isAdmin: boolean;
  updateSettings?: (newSettings: FeeSettings) => Promise<void>;
  login?: (email: string, password: string) => Promise<void>;
  logout?: () => Promise<void>;
}

const FirebaseFeeContext = createContext<FirebaseFeeContextType>({
  settings: null,
  loading: true,
  error: null,
  isAdmin: false
});

export function useFirebaseFees() {
  return useContext(FirebaseFeeContext);
}

const DEFAULT_SETTINGS: FeeSettings = {
  networkFees: {
    // BSC Mainnet
    56: {
      burnFee: "0.125",
      lockFee: "0.125",
      toolsFee: "0.125",
      scanFee: "0.05",
      masterLockFee: "0.25",
      ttTaxDistribution: "0.25",
      addLiquidityFee: "5",
      removeLiquidityFee: "0",
      multiSendFee: "0.0025",
      logoFee: "0.125"
    },
    // opBNB Mainnet
    204: {
      burnFee: "0.125",
      lockFee: "0.125",
      toolsFee: "0.125",
      scanFee: "0.05",
      masterLockFee: "0.25",
      ttTaxDistribution: "0.25",
      addLiquidityFee: "5",
      removeLiquidityFee: "0",
      multiSendFee: "0.0025",
      logoFee: "0.125"
    },
    // opBNB Testnet
    5611: {
      burnFee: "0.125",
      lockFee: "0.125",
      toolsFee: "0.125",
      scanFee: "0.05",
      masterLockFee: "0.25",
      ttTaxDistribution: "0.25",
      addLiquidityFee: "5",
      removeLiquidityFee: "0",
      multiSendFee: "0.0025",
      logoFee: "0.125"
    },
    // ETH Mainnet
    1: {
      burnFee: "0.01",
      lockFee: "0.01",
      toolsFee: "0.01",
      scanFee: "0.005",
      masterLockFee: "0.02",
      ttTaxDistribution: "0.02",
      addLiquidityFee: "2",
      removeLiquidityFee: "0",
      multiSendFee: "0.0001",
      logoFee: "0.01"
    },
    // Avalanche Mainnet
    43114: {
      burnFee: "0.5",
      lockFee: "0.5",
      toolsFee: "0.5",
      scanFee: "0.25",
      masterLockFee: "1",
      ttTaxDistribution: "1",
      addLiquidityFee: "5",
      removeLiquidityFee: "0",
      multiSendFee: "0.01",
      logoFee: "0.5"
    }
  },
  collectors: [
    {
      address: "0x760A0D5cC0ca242eB83609C627AB00Ac3F726Ba7",
      share: 100
    }
  ]
};

export function FirebaseFeeProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<FeeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isAdmin: walletIsAdmin } = useAdmin();
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize Firebase first
  useEffect(() => {
    async function init() {
      try {
        await initializeFirebase();
        setIsInitialized(true);
      } catch (err) {
        console.error("Failed to initialize Firebase:", err);
        setError("Failed to initialize Firebase");
        setLoading(false);
      }
    }
    init();
  }, []);

  // Listen for settings changes only after Firebase is initialized
  useEffect(() => {
    if (!isInitialized) return;

    let initialLoad = true;
    let retryCount = 0;
    const MAX_RETRIES = 3;

    const loadSettings = async () => {
      try {
        const docRef = doc(db, "settings", "fees");
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setSettings(docSnap.data() as FeeSettings);
          setLoading(false);
        } else if (initialLoad) {
          await setDoc(docRef, DEFAULT_SETTINGS);
          setSettings(DEFAULT_SETTINGS);
          setLoading(false);
        }
        initialLoad = false;
      } catch (err) {
        if (retryCount < MAX_RETRIES) {
          retryCount++;
          setTimeout(loadSettings, Math.pow(2, retryCount) * 1000);
        } else {
          setError("Failed to load fee settings after multiple attempts");
          setLoading(false);
        }
      }
    };

    loadSettings();

    const unsubscribe = onSnapshot(
      doc(db, "settings", "fees"),
      (doc) => {
        if (doc.exists()) {
          setSettings(doc.data() as FeeSettings);
          setLoading(false);
        }
      },
      (err) => {
        setError("Failed to sync fee settings");
      }
    );

    return () => unsubscribe();
  }, [isInitialized]);

  // Admin functions
  const updateSettings = async (newSettings: FeeSettings) => {
    if (!isInitialized) {
      throw new Error("Firebase not initialized");
    }
    
    if (!walletIsAdmin) {
      throw new Error("Unauthorized");
    }

    try {
      const docRef = doc(db, "settings", "fees");
      await setDoc(docRef, {
        ...newSettings,
        lastUpdated: new Date().toISOString()
      });
      
      // Force a refresh of the local state
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setSettings(docSnap.data() as FeeSettings);
      }
    } catch (err) {
      throw err;
    }
  };

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <FirebaseFeeContext.Provider 
      value={{ 
        settings, 
        loading, 
        error,
        isAdmin: walletIsAdmin,
        updateSettings: walletIsAdmin ? updateSettings : undefined,
        login: async (email: string, password: string) => {
          if (!isInitialized) {
            throw new Error("Firebase not initialized");
          }
          try {
            await signInWithEmailAndPassword(auth, email, password);
          } catch (err) {
            console.error("Login error:", err);
            throw err;
          }
        },
        logout: async () => {
          if (!isInitialized) {
            throw new Error("Firebase not initialized");
          }
          try {
            await signOut(auth);
          } catch (err) {
            console.error("Logout error:", err);
            throw err;
          }
        }
      }}
    >
      {children}
    </FirebaseFeeContext.Provider>
  );
}
